import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrgProfileProvider } from './contexts/OrgProfileContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorBoundary from './components/ErrorBoundary';
import Spinner from './components/Spinner';
import Signup from "./pages/Signup";
import Landing from "./pages/Landing";
import RoleSelection from "./pages/RoleSelection";
import ProtectedRoute from "./components/ProtectedRoute";

import ParentDashboard from './pages/ParentsHome.jsx';
import AdminDashboard from './pages/AdminDashboard';
import AdminWelcomeEmail from './components/admin/AdminWelcomeEmail';
import Dashboard from "./pages/Dashboard";
import AboutUs from './pages/AboutUs';
import ProfessionalProfile from './pages/ProfessionalProfile.jsx';
import ParentProfile from './pages/ParentProfile';
import OrganizationProfile from './pages/OrganizationProfile';
import ProfessionalDashboard from './pages/ProfessionalDashboard';
import OrganizationDashboard from './pages/OrganizationDashboard';
import JobDetail from './pages/jobs/JobDetail';
import MembershipPage from './pages/MembershipPage';
import FreeAssessmentPage from './pages/FreeAssessmentPage';
import MChatAssessment from './pages/MChatAssessment';
import AssessmentReport from './pages/AssessmentReport';
import PasswordReset from './pages/PasswordReset';
const JobSearchResults = React.lazy(() => import('./pages/JobSearchResults'));
// Smart redirector for dashboard
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

function normalizeRole(role) {
  if (!role) return '';
  const r = role.trim().toLowerCase().replace(/\s+/g, '_');
  if (["therapist","shadow_teacher","special_educator","psychologist","doctor"].includes(r)) return 'professional';
  if (["parent"].includes(r)) return 'parent';
  if (["center","clinic","cdc","school","inclusiveschool","hospital"].includes(r)) return 'organization';
  return r;
}

function DashboardRedirector() {
  const navigate = useNavigate();
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      navigate('/signup');
      return;
    }
    (async () => {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      let role = userSnap.exists() ? userSnap.data().role : '';
      const normRole = normalizeRole(role);
      if (normRole === 'parent') navigate('/app/dashboard/parent', { replace: true });
      else if (normRole === 'professional') navigate('/app/dashboard/professional', { replace: true });
      else if (normRole === 'organization') navigate('/app/dashboard/organization', { replace: true });
      else navigate('/signup');
    })();
  }, [navigate]);
  return null;
}

function ProfileRedirector() {
  const navigate = useNavigate();
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      navigate('/signup');
      return;
    }
    (async () => {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      let role = userSnap.exists() ? userSnap.data().role : '';
      const normRole = normalizeRole(role);
      if (normRole === 'parent') navigate('/app/profile/parent', { replace: true });
      else if (normRole === 'professional') navigate('/app/profile/professional', { replace: true });
      else if (normRole === 'organization') navigate('/app/profile/organization', { replace: true });
      else navigate('/signup');
    })();
  }, [navigate]);
  return null;
}

function App() {
  // Global error handler
  React.useEffect(() => {
    // Handle uncaught errors
    const handleError = (error) => {
      console.error('Uncaught error:', error);
      toast.error('An unexpected error occurred. Please refresh the page.');
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled rejection:', event.reason);
      toast.error('An unexpected error occurred. Please try again.');
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <OrgProfileProvider>
        <Router>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/app/role" element={<RoleSelection />} />

          <Route path="/app/jobs" element={
            <ProtectedRoute>
              <JobSearchResults />
            </ProtectedRoute>
          } />
          <Route path="/jobs/:jobId" element={
            <ProtectedRoute>
              <JobDetail />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/send-welcome-email" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminWelcomeEmail />
            </ProtectedRoute>
          } />
          {/* Role-specific dashboards */}
          <Route path="/app/dashboard/parent" element={
            <ProtectedRoute allowedRoles={['parent']}>
              <Suspense fallback={<Spinner fullScreen />}>
                <ParentDashboard />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/app/dashboard/professional" element={<ProfessionalDashboard />} />
          <Route 
            path="/app/dashboard/organization" 
            element={
              <Suspense fallback={<Spinner fullScreen />}>
                <OrganizationDashboard />
              </Suspense>
            } 
          />
          {/* Smart dashboard router */}
          <Route 
            path="/app/dashboard" 
            element={
              <Suspense fallback={<Spinner fullScreen />}>
                <DashboardRedirector />
              </Suspense>
            } 
          />
          {/* Role-specific profiles */}
          <Route path="/app/profile/parent" element={<ParentProfile />} />
          <Route path="/app/profile/professional" element={<ProfessionalProfile />} />
          <Route path="/app/profile/organization" element={<OrganizationProfile />} />
          {/* Smart profile router */}
          <Route path="/app/profile" element={<ProfileRedirector />} />
          
          {/* Job Search Results */}
          <Route path="/app/jobs" element={
            <Suspense fallback={<Spinner fullScreen />}>
              <JobSearchResults />
            </Suspense>
          } />
          
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/free-assessment" element={<FreeAssessmentPage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/mchat-assessment" element={<MChatAssessment />} />
          <Route path="/app/report/:assessmentId" element={<AssessmentReport />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <ToastContainer 
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        </Router>
      </OrgProfileProvider>
    </ErrorBoundary>
  );
}

export default App;
